'use client';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { ITestimonial, TESTIMONIALS } from '@/lib/testimonials';

/** How long each review stays on screen before the next one fades in. */
const ROTATE_MS = 6000;

/**
 * How long the outgoing review takes to fade out. The next review is swapped in
 * only once that has finished: the arrows sit directly beneath the review, so
 * swapping any earlier would move them across text that is still on screen.
 * Keep in step with the `duration-*` class on the figure.
 */
const FADE_MS = 300;

interface ITestimonials {
	className?: string;
	testimonials?: ITestimonial[];
}

export default function Testimonials(props: ITestimonials) {
	const testimonials = props.testimonials ?? TESTIMONIALS;
	const [active, setActive] = useState(0);
	/** The review currently laid out, which lags `active` by one fade. */
	const [shown, setShown] = useState(0);
	const [isFaded, setIsFaded] = useState(false);
	const [paused, setPaused] = useState(false);

	const go = useCallback(
		(step: number) => {
			setActive((current) => (current + step + testimonials.length) % testimonials.length);
		},
		[testimonials.length]
	);

	// Auto-advance, unless the visitor is interacting with the section or has asked
	// for reduced motion. Keyed on `active`, so using the arrows restarts the full
	// interval instead of leaving a part-elapsed one to cut the review short.
	useEffect(() => {
		if (paused || testimonials.length < 2) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const timer = setTimeout(() => {
			setActive((active + 1) % testimonials.length);
		}, ROTATE_MS);

		return () => clearTimeout(timer);
	}, [active, paused, testimonials.length]);

	// Fade the outgoing review out, then lay the next one out while nothing is
	// visible, so the arrows move only across empty space.
	useEffect(() => {
		if (active === shown) return;

		setIsFaded(true);
		const swap = setTimeout(() => setShown(active), FADE_MS);
		return () => clearTimeout(swap);
	}, [active, shown]);

	// Fade the new review back in a frame after it lands: dropping the opacity
	// class in the same paint as the swap would skip the transition entirely.
	useEffect(() => {
		if (!isFaded || shown !== active) return;

		let second = 0;
		const first = requestAnimationFrame(() => {
			second = requestAnimationFrame(() => setIsFaded(false));
		});
		return () => {
			cancelAnimationFrame(first);
			cancelAnimationFrame(second);
		};
	}, [shown, active, isFaded]);

	if (!testimonials.length) {
		return null;
	}

	return (
		<section
			aria-label="What our customers say"
			className={`container h-[580px] md:h-[800px] ${props.className ?? ''}`}
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
			onFocus={() => setPaused(true)}
			onBlur={() => setPaused(false)}
		>
			{/*
			 * The arrows sit 2rem beneath the review on mobile and flank it from md up.
			 * `md:contents` dissolves the mobile control row so the same two buttons
			 * become flex items of this row, ordered either side of the quote. The
			 * mobile padding keeps 4rem of clear space above and below the group.
			 */}
			<div className="flex h-full flex-col items-center justify-center gap-8 py-16 md:flex-row md:justify-between md:gap-10 md:py-0">
				{/*
				 * Every review stays in the markup, stacked on top of each other, but
				 * only the one on screen is in flow: the arrows below it then keep
				 * their 2rem gap whatever the length of the review, and the section's
				 * fixed height means the page never moves as reviews rotate.
				 */}
				<div className="relative order-1 w-full md:order-2 md:flex-1">
					{testimonials.map((testimonial, index) => {
						const isShown = index === shown;

						return (
							<figure
								key={testimonial.reviewUrl}
								aria-hidden={!isShown}
								className={`mx-auto flex max-w-6xl flex-col items-center text-center transition-opacity duration-300 motion-reduce:transition-none ${
									isShown
										? `relative ${isFaded ? 'opacity-0' : 'opacity-100'}`
										: 'pointer-events-none absolute inset-x-0 top-0 opacity-0'
								}`}
							>
								<blockquote className="text-2xl md:text-3xl lg:text-5xl font-bold leading-tight md:leading-snug text-[#0A0A0A]">
									<span aria-hidden>“</span>
									<HighlightedQuote
										quote={testimonial.quote}
										highlight={testimonial.highlight}
									/>
									<span aria-hidden>”</span>
								</blockquote>

								<figcaption className="mt-4 md:mt-8 text-[16px] md:text-[18px] font-bold text-gray-500">
									<a
										href={testimonial.reviewUrl}
										target="_blank"
										rel="noopener noreferrer"
										tabIndex={isShown ? undefined : -1}
										className="hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
									>
										{testimonial.author}
										<span className="sr-only">
											{' '}
											— read this review on Google
										</span>
									</a>
								</figcaption>
							</figure>
						);
					})}
				</div>

				{testimonials.length > 1 ? (
					<div className="order-2 flex items-center gap-4 md:contents">
						<ArrowButton
							className="md:order-1"
							label="Previous testimonial"
							onClick={() => go(-1)}
						>
							<ArrowLeft className="h-5 w-5 md:h-4 md:w-4" aria-hidden />
						</ArrowButton>
						<ArrowButton
							className="md:order-3"
							label="Next testimonial"
							onClick={() => go(1)}
						>
							<ArrowRight className="h-5 w-5 md:h-4 md:w-4" aria-hidden />
						</ArrowButton>
					</div>
				) : null}
			</div>
		</section>
	);
}

/**
 * Renders the review with its `highlight` phrase picked out in blue. The phrase
 * is located in the quote rather than appended to it, so the design can emphasise
 * a clause anywhere in the review — George's sits two sentences from the end.
 * A phrase that no longer matches falls back to an unemphasised quote.
 */
function HighlightedQuote(props: { quote: string; highlight: string }) {
	const start = props.highlight ? props.quote.indexOf(props.highlight) : -1;

	if (start === -1) {
		return <>{props.quote}</>;
	}

	return (
		<>
			{props.quote.slice(0, start)}
			<span className="text-primary-500">{props.highlight}</span>
			{props.quote.slice(start + props.highlight.length)}
		</>
	);
}

function ArrowButton(props: {
	label: string;
	onClick: () => void;
	className?: string;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			aria-label={props.label}
			onClick={props.onClick}
			className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[#0A0A0A] transition-colors hover:bg-[#E5E5E5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 md:h-10 md:w-10 ${
				props.className ?? ''
			}`}
		>
			{props.children}
		</button>
	);
}
